/**
 * Shared type definitions and utilities for form context handling
 * Used by LinkBuilder and LinkRefTagBuilder components
 */

/**
 * Type definition for the expected formContext structure
 * Used to access creativeName and other metadata for ref tag generation
 */
export type FormContextData = {
  flavors: Array<{
    creativeName?: string;
    content: {
      arguments: Record<string, unknown>;
    };
  }>;
  currentFlavorIndex: number;
  marketplace?: string;
  platform?: string;
};

/**
 * Type guard to safely check if formContext matches expected structure
 *
 * @param context - Unknown context object to validate
 * @returns True if context matches FormContextData structure
 */
export const isValidFormContext = (context: unknown): context is FormContextData => {
  if (context === null || context === undefined || typeof context !== 'object') {
    return false;
  }

  const ctx = context as Record<string, unknown>;

  return (
    Array.isArray(ctx.flavors) &&
    typeof ctx.currentFlavorIndex === 'number' &&
    ctx.currentFlavorIndex >= 0 &&
    ctx.currentFlavorIndex < (ctx.flavors as unknown[]).length
  );
};

/**
 * Extracts the creativeName from formContext for the current flavor
 * Returns empty string if context is invalid or creativeName is not present
 *
 * @param formContext - The form context object
 * @returns The creative name or empty string
 */
export const getCreativeName = (formContext: unknown): string => {
  if (!isValidFormContext(formContext)) {
    return '';
  }
  return formContext.flavors[formContext.currentFlavorIndex]?.creativeName || '';
};
